import type { Graph, Thing, WebPage, WithContext } from 'schema-dts'
import { useServerHead, useState } from '#imports'

/**
 * Composable for working with application/ld+json data
 */
export const useSeoSchema = () => {
    const state = useState<Array<Thing>>('seo:structuredData', () => [])

    if (import.meta.client) {
        return {
            state,
            add() {},
            describeWebPage() {},
        }
    }

    function add(value: Thing) {
        state.value.push(value)
    }

    /**
     * Gives the page's `WebPage` node, which the seo-graph plugin adds for every
     * page, a name and description. Empty values are left out.
     */
    function describeWebPage({ name, description }: { name?: string, description?: string }) {
        const webPage = state.value.find((node): node is Exclude<WebPage, string> =>
            typeof node === 'object' && (node as { '@type'?: unknown })['@type'] === 'WebPage')
        if (!webPage) return

        if (name) webPage.name = name
        if (description) webPage.description = description
    }

    useServerHead(() => {
        if (!state.value.length) return {}

        const [first] = state.value
        let schema: WithContext<Thing> | Graph

        if (state.value.length === 1 && typeof first === 'object') {
            schema = {
                '@context': 'https://schema.org',
                ...first,
            } as WithContext<Thing>
        }
        else {
            schema = {
                '@context': 'https://schema.org',
                '@graph': state.value,
            } satisfies Graph
        }

        return {
            script: [
                {
                    type: 'application/ld+json',
                    id: 'structured-data',
                    innerHTML: JSON.stringify(schema),
                },
            ],
        }
    })

    return {
        state,
        add,
        describeWebPage,
    }
}
